import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Wallet, User, LogOut, Shield, LayoutDashboard, Coins, Users, Bell, Rocket } from 'lucide-react';
import ThemeToggle from '@/components/ThemeToggle';
import NotificationBell from '@/components/NotificationBell';
import StakingAnnouncementBell from '@/components/StakingAnnouncementBell';

const navItems = [
  { to: '/markets', label: 'Markets', testid: 'nav-markets' },
  { to: '/trade/bitcoin', label: 'Spot', testid: 'nav-trade' },
  { to: '/futures', label: 'Futures', testid: 'nav-futures' },
  { to: '/staking', label: 'Staking', testid: 'nav-staking' },
  { to: '/launchpad', label: 'Launchpad', testid: 'nav-launchpad' },
  { to: '/traders', label: 'Copy', testid: 'nav-traders' },
  { to: '/battles', label: 'Battles', testid: 'nav-battles' },
  { to: '/marketplace/templates', label: 'Marketplace', testid: 'nav-marketplace' },
  { to: '/wallet', label: 'Wallet', testid: 'nav-wallet', authOnly: true },
];

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0E11]/85 border-b border-[#2B3139]">
      <div className="max-w-[1440px] mx-auto flex items-center h-16 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2 mr-8" data-testid="logo-link">
          <img src="/bitgonode-logo.png" alt="BITGONODE" className="h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-6 flex-1">
          {navItems.map(item => (
            (!item.authOnly || user) && (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${isActive ? 'text-[#FCD535]' : 'text-[#B7BDC6] hover:text-white'}`
                }
              >
                {item.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="flex items-center gap-3 ml-auto">
          <StakingAnnouncementBell />
          <ThemeToggle />
          {user ? (
            <>
              <NotificationBell />
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-[#EAECEF] hover:text-white hover:bg-[#2B3139]" data-testid="dashboard-btn">
                  <LayoutDashboard className="w-4 h-4 mr-1.5" />Dashboard
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full bg-[#2B3139] hover:bg-[#323842] px-3 py-1.5 transition-colors" data-testid="user-menu-trigger">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#FCD535] grid place-items-center text-[#0B0E11] font-bold text-xs">
                        {user.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="text-sm text-white hidden sm:inline">{user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#181A20] border-[#2B3139] text-[#EAECEF] min-w-[180px]">
                  <DropdownMenuItem
                    onClick={() => navigate(`/u/${user.username || user.referral_code || ''}`)}
                    data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" /> My Profile
                    {user.username && <span className="text-[10px] font-mono text-[#FCD535] ml-1">@{user.username}</span>}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#2B3139]" />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/wallet')} data-testid="menu-wallet">
                    <Wallet className="w-4 h-4 mr-2" /> Wallet
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/staking')} data-testid="menu-staking">
                    <Coins className="w-4 h-4 mr-2" /> Staking
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/referrals')} data-testid="menu-referrals">
                    <Users className="w-4 h-4 mr-2" /> Refer & Earn
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/alerts')} data-testid="menu-alerts">
                    <Bell className="w-4 h-4 mr-2" /> Price Alerts
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-listings')} data-testid="menu-my-listings">
                    <Rocket className="w-4 h-4 mr-2" /> My Listings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/kyc')} data-testid="menu-kyc">
                    <User className="w-4 h-4 mr-2" /> KYC ({user.kyc_status})
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <Shield className="w-4 h-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#2B3139]" />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate('/'); }} data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-[#EAECEF] hover:text-white hover:bg-[#2B3139]" data-testid="header-login">Log In</Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-md" data-testid="header-signup">Sign Up</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
